// List of generated/uploaded documents for an appointment, with sign + open
// actions. Used inside the appointment row's secondary cell.

import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  CheckCircle2,
  CornerDownRight,
  Download,
  FileText,
  Link2,
  Loader2,
  PenLine,
  Plus,
  Send,
  Undo2,
  Upload, Sparkles } from 'lucide-react';
import {
  type DocumentRow,
  fetchDocumentSignature,
  parseAvailableLanguages,
  useCreateMagicLink,
  useDocumentsForAppointment,
} from '@/hooks/data/useDocuments';
import { SignDocumentDialog } from '@/components/documents/SignDocumentDialog';
import { SendToClientDialog } from '@/components/documents/SendToClientDialog';
import { TranslateDocumentDialog } from '@/components/documents/TranslateDocumentDialog';
import { AppointmentAttachments } from './AppointmentAttachments';
import { useAuth } from '@/hooks/useAuth';
import { apiFetch } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

// Phase E — per-language download chips. The 10 supported language codes
// are listed in `pdf/strings/types.ts`; this map keeps the chip ordering
// stable (DE first as the binding doc, then alphabetical) and provides
// the short 2-letter label rendered in the pill.
const LANG_LABEL: Record<string, string> = {
  de: 'DE',
  en: 'EN',
  fr: 'FR',
  it: 'IT',
  sr: 'SR',
  bg: 'BG',
  ru: 'RU',
  tr: 'TR',
  ar: 'AR',
  zh: 'ZH',
};

const LANG_ORDER = ['de', 'en', 'fr', 'it', 'sr', 'bg', 'ru', 'tr', 'ar', 'zh'];

function sortLangs(langs: string[]): string[] {
  const set = new Set(langs);
  return LANG_ORDER.filter((l) => set.has(l));
}

const TYPE_FILENAME: Record<DocumentRow['type'], string> = {
  vermittlungsauftrag: 'Vermittlungsauftrag',
  besichtigungsschein: 'Besichtigungsschein',
  kaufanbot: 'Kaufanbot',
};

async function downloadLanguageVariant(
  doc: DocumentRow,
  lang: string,
): Promise<void> {
  const res = await apiFetch(
    `/documents/${doc.id}/pdf?lang=${encodeURIComponent(lang)}`,
  );
  if (!res.ok) {
    const j = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(j.message ?? `download_failed_${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${TYPE_FILENAME[doc.type]}-${doc.number}-${lang}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
// PDF helpers loaded on demand — see offers.$id.tsx for the same pattern.
type PdfModule = typeof import('@/pdf/offerPdfGenerator');
const loadPdf = (): Promise<PdfModule> => import('@/pdf/offerPdfGenerator');
import {
  useDocumentDrafts,
  useConsumeDocumentDraft,
  useDismissDocumentDraft,
} from '@/hooks/data/useDocumentDrafts';
import { GenerateDocumentButton } from '@/components/appointments/GenerateDocumentButton';
import { useAppointment } from '@/hooks/data/useAppointments';
import { toMs } from '@/lib/appointmentDate';
import type { ClientRow } from '@/hooks/data/useClients';
import type { PropertyRow } from '@/hooks/data/useProperties';

const TYPE_LABEL: Record<DocumentRow['type'], string> = {
  vermittlungsauftrag: 'Vermittlungsauftrag',
  besichtigungsschein: 'Besichtigungsschein',
  kaufanbot: 'Kaufanbot',
};

interface Props {
  appointmentId: string;
}

export function AppointmentDocuments({ appointmentId }: Props) {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { data: docs = [] } = useDocumentsForAppointment(appointmentId);
  const [signing, setSigning] = useState<DocumentRow | null>(null);
  const [sending, setSending] = useState<DocumentRow | null>(null);
  const [translating, setTranslating] = useState<DocumentRow | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  // Tracks `<docId>:<lang>` of the language chip currently downloading.
  const [downloadingLang, setDownloadingLang] = useState<string | null>(null);
  // Batch AI-3 — paper-signed PDF upload tracking.
  const [uploadingSignedId, setUploadingSignedId] = useState<string | null>(null);
  const uploadFileInputRef = useRef<HTMLInputElement>(null);
  const pendingUploadDocRef = useRef<DocumentRow | null>(null);
  // Batch AN — Gegenanbot upload dialog state.
  const [gegenanbotFor, setGegenanbotFor] = useState<DocumentRow | null>(null);

  const onDownloadLang = async (doc: DocumentRow, lang: string) => {
    const key = `${doc.id}:${lang}`;
    setDownloadingLang(key);
    try {
      await downloadLanguageVariant(doc, lang);
    } catch (cause) {
      toast.error((cause as Error).message);
    } finally {
      setDownloadingLang(null);
    }
  };
  const magicLink = useCreateMagicLink();
  const queryClient = useQueryClient();

  // Batch AI-3 — Trigger hidden file picker, then POST the file as PDF
  // bytes to /documents/:id/uploaded-signed. On success the doc flips to
  // signed + locked and the workflow event fires server-side.
  const onUploadSigned = (doc: DocumentRow) => {
    pendingUploadDocRef.current = doc;
    uploadFileInputRef.current?.click();
  };
  const onUploadSignedFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const doc = pendingUploadDocRef.current;
    e.target.value = '';
    pendingUploadDocRef.current = null;
    if (!file || !doc) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error(t('docs.upload_signed.must_be_pdf', 'Nur PDF-Dateien sind erlaubt.'));
      return;
    }
    setUploadingSignedId(doc.id);
    try {
      const res = await apiFetch(`/documents/${doc.id}/uploaded-signed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/pdf' },
        body: file,
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(j.message ?? `upload_failed_${res.status}`);
      }
      toast.success(
        t('docs.upload_signed.done', 'Unterschriebenes Dokument hochgeladen.'),
      );
      // Refresh the document list so the row flips to signed.
      queryClient.invalidateQueries({ queryKey: ['documents', 'appointment', appointmentId] });
    } catch (cause) {
      toast.error((cause as Error).message);
    } finally {
      setUploadingSignedId(null);
    }
  };

  const onDownloadSigned = async (doc: DocumentRow) => {
    if (!profile) return;
    setDownloadingId(doc.id);
    try {
      // Batch AI-1 — PDF lock-after-sign. If the server has the locked
      // signed-PDF blob pinned, fetch and download it directly without
      // re-rendering. Guarantees the bytes match exactly what the client
      // signed, even if the underlying client/property data has changed
      // since signing.
      if (doc.signed_pdf_storage_key) {
        const res = await apiFetch(`/documents/${doc.id}/signed-pdf`);
        if (res.ok) {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const docTitle =
            doc.type === 'vermittlungsauftrag'
              ? doc.contract_variant === 'einfach'
                ? 'Vermittlungsauftrag'
                : 'Alleinvermittlungsauftrag'
              : doc.type === 'besichtigungsschein'
                ? 'Besichtigungsschein'
                : 'Kaufanbot';
          a.download = `${docTitle}-${doc.number}-signed.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
          toast.success(t('docs.download.done', 'PDF heruntergeladen.'));
          return;
        }
        // Fall through to the legacy re-render path if the blob is
        // unreachable for any reason (R2 missing, transient 5xx).
      }

      const [signatureDataUrl, client, property] = await Promise.all([
        fetchDocumentSignature(doc.id),
        doc.client_id
          ? (apiFetch(`/clients/${doc.client_id}`).then((r) =>
              r.ok ? (r.json() as Promise<ClientRow>) : null,
            ) as Promise<ClientRow | null>)
          : Promise.resolve<ClientRow | null>(null),
        doc.property_id
          ? (apiFetch(`/properties/${doc.property_id}`).then((r) =>
              r.ok ? (r.json() as Promise<PropertyRow>) : null,
            ) as Promise<PropertyRow | null>)
          : Promise.resolve<PropertyRow | null>(null),
      ]);
      if (!client) throw new Error('client_missing');
      const signedAt = doc.signed_at ?? Date.now();
      const date = doc.created_at;
      const pdf = await loadPdf();
      // Helper to seal the freshly-rendered PDF into the server-side
      // lock if the document is signed but hasn't been pinned yet. This
      // is the legacy-document upgrade path — the next download will
      // come from R2 directly. Fire-and-forget; never blocks the user.
      const lazyLock = (blob: Blob) => {
        if (!doc.signed || doc.signed_pdf_storage_key) return;
        void apiFetch(`/documents/${doc.id}/lock-pdf`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/pdf' },
          body: blob,
        }).catch(() => {
          /* lazy lock is best-effort */
        });
      };
      const triggerDownload = (blob: Blob, filename: string) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      };
      if (doc.type === 'vermittlungsauftrag') {
        if (!property) throw new Error('property_missing');
        // Batch AJ-6 — pull the persisted edit-dialog payload out of
        // documents.metadata if present, so a re-render reproduces the
        // operator's original choices (provision %, Laufzeit, all the
        // accordion fields). Documents created before AJ-6 don't have
        // this row, so fall back to the previous safe defaults.
        let meta: {
          vermittlungsauftrag?: {
            contractVariant?: 'allein' | 'einfach';
            provisionNetPercent: number | null;
            exclusivityMonths: number;
            gerichtsstand: string | null;
            seller: Record<string, unknown>;
            property: Record<string, unknown>;
            bestaetigung: Record<string, unknown>;
            faggVorzeitig: boolean | null;
          };
        } = {};
        try {
          if (doc.metadata) meta = JSON.parse(doc.metadata) as typeof meta;
        } catch {
          // bad JSON — fall through to defaults
        }
        const va = meta.vermittlungsauftrag;
        const vaData = {
          documentNumber: doc.number,
          agentName: profile.full_name,
          date,
          seller: {
            title: client.title,
            first_name: client.first_name,
            last_name: client.last_name,
            address: client.address,
            location: client.location,
            phone: client.phone,
            email: client.email,
            birth_date: client.birth_date,
            citizenship: client.citizenship,
            sv_number: client.sv_number,
            ...(va?.seller ?? {}),
          },
          property: {
            address: property.address,
            city: property.city,
            postal_code: property.postal_code,
            district: property.district,
            entrance: property.entrance,
            floor: property.floor,
            door_number: property.door_number,
            object_type: property.object_type,
            building_type: property.building_type,
            rooms_count: property.rooms_count,
            property_size: property.property_size,
            property_area: property.property_area,
            purchase_price: property.purchase_price,
            edi_real_number: property.edi_real_number,
            kg_grundbuch: property.kg_grundbuch,
            einlagezahl: property.einlagezahl,
            b_laufnummer: property.b_laufnummer,
            ...(va?.property ?? {}),
          },
          // Column first (migration 0058 backfilled every historical row to
          // 'allein'), metadata as the fallback, 'allein' as the floor — a
          // re-render must never turn a signed exclusive contract into an
          // ordinary one.
          contractVariant: doc.contract_variant ?? va?.contractVariant ?? 'allein',
          provisionNetPercent: va?.provisionNetPercent ?? null,
          ustPercent: 20,
          exclusivityMonths: va?.exclusivityMonths ?? 6,
          gerichtsstand: va?.gerichtsstand ?? null,
          bestaetigung: va?.bestaetigung,
          faggVorzeitig: va?.faggVorzeitig ?? null,
          signatureDataUrl: signatureDataUrl ?? undefined,
          signedAt,
        };
        const blob = await pdf.generateVermittlungsauftragBlob(vaData);
        triggerDownload(blob, `Vermittlungsauftrag-${doc.number}.pdf`);
        lazyLock(blob);
      } else if (doc.type === 'besichtigungsschein') {
        const bsData = {
          documentNumber: doc.number,
          agentName: profile.full_name,
          date,
          buyer: {
            title: client.title,
            first_name: client.first_name,
            last_name: client.last_name,
            address: client.address,
            location: client.location,
            phone: client.phone,
            email: client.email,
          },
          property: property
            ? {
                address: property.address,
                city: property.city,
                postal_code: property.postal_code,
                district: property.district,
                entrance: property.entrance,
                floor: property.floor,
                door_number: property.door_number,
                object_type: property.object_type,
                edi_real_number: property.edi_real_number,
              }
            : {
                address: null,
                city: '—',
                postal_code: null,
                district: null,
                entrance: null,
                floor: null,
                door_number: null,
                object_type: null,
                edi_real_number: null,
              },
          signatureDataUrl: signatureDataUrl ?? undefined,
          signedAt,
        };
        const blob = await pdf.generateBesichtigungsscheinBlob(bsData);
        triggerDownload(blob, `Besichtigungsschein-${doc.number}.pdf`);
        lazyLock(blob);
      }
      toast.success(t('docs.download.done', 'PDF heruntergeladen.'));
    } catch (cause) {
      toast.error((cause as Error).message);
    } finally {
      setDownloadingId(null);
    }
  };

  const onSendLink = async (doc: DocumentRow) => {
    try {
      const result = await magicLink.mutateAsync({ id: doc.id });
      if (result.sms.status === 'sent') {
        toast.success(t('docs.magic.sms_sent', 'Magic-Link per SMS gesendet.'));
      } else if (result.sms.status === 'skipped') {
        navigator.clipboard?.writeText(result.url).catch(() => {});
        toast.success(t('docs.magic.copied', 'Link kopiert: {{url}}', { url: result.url }), {
          duration: 12000,
        });
      } else {
        toast.error(
          `${t('docs.magic.sms_failed', 'SMS fehlgeschlagen')}: ${result.sms.error ?? '—'}. Link: ${result.url}`,
          { duration: 15000 },
        );
      }
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  // Batch AH faza 3d — render even when no generated docs exist so the
  // manual attachment uploader is always reachable from the appointment.
  return (
    <>
      <ZokiDraftBanner appointmentId={appointmentId} />
      {docs.length > 0 && (
      <ul className="mt-1 space-y-1">
        {docs.map((d) => {
          const langs = sortLangs(parseAvailableLanguages(d.available_languages));
          const missingLangs = LANG_ORDER.filter((l) => !langs.includes(l));
          return (
          <li
            key={d.id}
            className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-paper-2 px-2 py-1 text-[11px]"
          >
            <FileText className="h-3 w-3 text-ink-3" />
            <span className="font-medium text-ink inline-flex items-center gap-1">
              {d.parent_document_id && (
                <CornerDownRight
                  className="h-3 w-3 text-[hsl(var(--brand-gold-deep))]"
                  aria-label="Gegenanbot"
                />
              )}
              {d.parent_document_id
                ? t('docs.gegenanbot.label', 'Gegenanbot')
                : TYPE_LABEL[d.type]}
            </span>
            {/* Two contracts now share one type + one number sequence, so the
                row has to say which one it is. */}
            {d.type === 'vermittlungsauftrag' && !d.parent_document_id && (
              <span
                className={
                  'rounded px-1.5 py-0.5 text-[10px] font-medium ' +
                  (d.contract_variant === 'einfach'
                    ? 'bg-paper-3 text-ink-2'
                    : 'bg-[hsl(var(--brand-gold))]/15 text-[hsl(var(--brand-gold-deep))]')
                }
              >
                {d.contract_variant === 'einfach'
                  ? t('docs.variant.einfach_short', 'Einfach')
                  : t('docs.variant.allein_short', 'Exklusiv')}
              </span>
            )}
            <span className="font-mono text-ink-3">Nr. {d.number}</span>
            <span className="ms-auto inline-flex flex-wrap gap-1">
              {d.signed ? (
                <>
                  <span className="inline-flex items-center gap-1 text-[10px] text-[hsl(var(--positive))]">
                    <CheckCircle2 className="h-3 w-3" />
                    {t('docs.signed', 'unterschrieben')}
                  </span>
                  <button
                    type="button"
                    onClick={() => onDownloadSigned(d)}
                    disabled={downloadingId === d.id}
                    title={t('docs.download.signed', 'Signed PDF herunterladen')}
                    className="inline-flex h-6 w-6 items-center justify-center rounded text-ink-2 hover:bg-paper-3 disabled:opacity-50"
                  >
                    <Download className="h-3 w-3" />
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setSigning(d)}
                    className="inline-flex items-center gap-1 rounded-md border border-line bg-card px-1.5 py-0.5 text-[10px] font-medium hover:bg-paper-3"
                  >
                    <PenLine className="h-3 w-3" />
                    {t('docs.sign_cta', 'Unterschreiben')}
                  </button>
                  <button
                    type="button"
                    onClick={() => onSendLink(d)}
                    disabled={magicLink.isPending}
                    title={t('docs.magic.title', 'Magic-Link an Klient senden')}
                    className="inline-flex items-center gap-1 rounded-md border border-line bg-card px-1.5 py-0.5 text-[10px] font-medium hover:bg-paper-3 disabled:opacity-50"
                  >
                    <Link2 className="h-3 w-3" />
                    {t('docs.magic.cta', 'Magic-Link')}
                  </button>
                  {/* Batch AI-3 — paper-signed upload. The maker scans/photos
                      the in-person signed PDF and uploads here; the document
                      becomes signed + locked in one shot, same final state
                      as the online flow. */}
                  <button
                    type="button"
                    onClick={() => onUploadSigned(d)}
                    disabled={uploadingSignedId === d.id}
                    title={t('docs.upload_signed.title', 'Unterschriebenes PDF hochladen')}
                    className="inline-flex items-center gap-1 rounded-md border border-line bg-card px-1.5 py-0.5 text-[10px] font-medium hover:bg-paper-3 disabled:opacity-50"
                  >
                    <Upload className="h-3 w-3" />
                    {uploadingSignedId === d.id
                      ? t('common.loading', 'Lädt …')
                      : t('docs.upload_signed.cta', 'Unterschrieben hochladen')}
                  </button>
                </>
              )}
              {/* Plan v2 update 1: send to client (works for signed + unsigned) */}
              <button
                type="button"
                onClick={() => setSending(d)}
                title={t('docs.send.title', 'An Klient senden')}
                className="inline-flex items-center gap-1 rounded-md border border-line bg-card px-1.5 py-0.5 text-[10px] font-medium hover:bg-paper-3"
              >
                <Send className="h-3 w-3" />
                {t('docs.send.cta', 'Senden')}
              </button>
              {/* Batch AN — seller's Gegenanbot upload. Only shown on
                  Kaufanbot rows that are NOT themselves a Gegenanbot
                  (no parent_document_id) and that have an offer link. */}
              {d.type === 'kaufanbot' && !d.parent_document_id && d.offer_id && (
                <button
                  type="button"
                  onClick={() => setGegenanbotFor(d)}
                  title={t(
                    'docs.gegenanbot.title',
                    'Gegenanbot vom Verkäufer hochladen (unterschriebenes PDF)',
                  )}
                  className="inline-flex items-center gap-1 rounded-md border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold)/0.10)] px-1.5 py-0.5 text-[10px] font-medium text-[hsl(var(--brand-gold-deep))] hover:bg-[hsl(var(--brand-gold)/0.18)]"
                >
                  <Undo2 className="h-3 w-3" />
                  {t('docs.gegenanbot.cta', 'Gegenanbot hochladen')}
                </button>
              )}
            </span>
            {/* Phase E — per-language download chips. Always shows DE.
                Other languages appear as the background queue uploads
                them (typically within ~15-20s of generation). */}
            {langs.length > 0 && (
              <span
                className="basis-full inline-flex flex-wrap items-center gap-1 pt-1"
                title={t(
                  'docs.langs.title',
                  'Verfügbare Sprachen — klicken zum Herunterladen',
                )}
              >
                <span className="text-[9px] uppercase tracking-wide text-ink-3">
                  {t('docs.langs.label', 'Sprachen')}
                </span>
                {langs.map((lang) => {
                  const key = `${d.id}:${lang}`;
                  const isLoading = downloadingLang === key;
                  const isDe = lang === 'de';
                  return (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => onDownloadLang(d, lang)}
                      disabled={isLoading}
                      title={t(`docs.langs.${lang}`, LANG_LABEL[lang] ?? lang)}
                      className={
                        isDe
                          ? 'inline-flex items-center gap-1 rounded border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold)/.08)] px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase text-ink hover:bg-[hsl(var(--brand-gold)/.16)] disabled:opacity-50'
                          : 'inline-flex items-center gap-1 rounded border border-line bg-card px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase text-ink-2 hover:bg-paper-3 disabled:opacity-50'
                      }
                    >
                      {isLoading ? (
                        <Loader2 className="h-2.5 w-2.5 animate-spin" />
                      ) : (
                        <Download className="h-2.5 w-2.5 opacity-60" />
                      )}
                      {LANG_LABEL[lang] ?? lang.toUpperCase()}
                    </button>
                  );
                })}
                {/* TODO 13.6 — retroactive translation. Show "+ N" chip
                    when fewer than 10 languages exist; click opens
                    TranslateDocumentDialog with the missing langs
                    pre-checked. */}
                {missingLangs.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setTranslating(d)}
                    title={t(
                      'docs.translate.add',
                      'Übersetzungen nachträglich hinzufügen',
                    )}
                    className="inline-flex items-center gap-1 rounded border border-dashed border-[hsl(var(--brand-gold))]/60 bg-[hsl(var(--brand-gold)/.04)] px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase text-[hsl(var(--brand-gold-deep))] hover:bg-[hsl(var(--brand-gold)/.12)]"
                  >
                    <Plus className="h-2.5 w-2.5" />
                    {missingLangs.length}
                  </button>
                )}
              </span>
            )}
          </li>
          );
        })}
      </ul>
      )}

      <AppointmentAttachments appointmentId={appointmentId} />

      {/* Batch AI-3 — hidden file input feeding the paper-signed upload
          flow. The click is triggered programmatically from onUploadSigned. */}
      <input
        ref={uploadFileInputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={onUploadSignedFileChosen}
      />

      {signing && (
        <SignDocumentDialog
          document={signing}
          open={true}
          onOpenChange={(o) => !o && setSigning(null)}
        />
      )}
      {sending && (
        <SendToClientDialog
          document={sending}
          open={true}
          onOpenChange={(o) => !o && setSending(null)}
        />
      )}
      {translating && (
        <TranslateDocumentDialog
          doc={translating}
          missingLangs={LANG_ORDER.filter(
            (l) => !sortLangs(parseAvailableLanguages(translating.available_languages)).includes(l),
          )}
          open={true}
          onOpenChange={(o) => !o && setTranslating(null)}
        />
      )}
      {gegenanbotFor && (
        <GegenanbotUploadDialog
          sourceDoc={gegenanbotFor}
          appointmentId={appointmentId}
          open={true}
          onOpenChange={(o) => !o && setGegenanbotFor(null)}
        />
      )}
    </>
  );
}

// Batch AN — Gegenanbot upload dialog. Operator picks the seller-signed
// counter-offer PDF, optionally enters the agreed price + a note, then
// POSTs to /documents/:id/counter-offer. Backend mints a child doc,
// flips the parent offer to counter_offered, generates magic-link sign
// tokens for every buyer, and SMS them the link. On success we toast
// the per-buyer SMS status and invalidate the documents list so the
// new Gegenanbot row appears with the ↳ indicator.
function GegenanbotUploadDialog({
  sourceDoc,
  appointmentId,
  open,
  onOpenChange,
}: {
  sourceDoc: DocumentRow;
  appointmentId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [priceEur, setPriceEur] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    if (!file) {
      setErr(t('docs.gegenanbot.errors.file_required', 'Bitte PDF-Datei auswählen.'));
      return;
    }
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setErr(t('docs.gegenanbot.errors.must_be_pdf', 'Nur PDF-Dateien sind erlaubt.'));
      return;
    }
    const headers: Record<string, string> = { 'Content-Type': 'application/pdf' };
    if (priceEur.trim()) {
      const eur = Number(priceEur.replace(/\./g, '').replace(',', '.'));
      if (!Number.isFinite(eur) || eur < 0) {
        setErr(t('docs.gegenanbot.errors.bad_price', 'Ungültiger Preis.'));
        return;
      }
      // EUR cents to match offers.offered_price storage convention.
      headers['x-gegenanbot-price'] = String(Math.round(eur * 100));
    }
    if (note.trim()) headers['x-gegenanbot-note'] = note.trim();
    setSubmitting(true);
    try {
      const res = await apiFetch(`/documents/${sourceDoc.id}/counter-offer`, {
        method: 'POST',
        headers,
        body: file,
      });
      const json = (await res.json().catch(() => ({}))) as {
        message?: string;
        links?: Array<{
          client_name: string;
          sms: { status: 'sent' | 'failed' | 'skipped'; error?: string };
        }>;
        buyers_count?: number;
      };
      if (!res.ok) throw new Error(json.message ?? `gegenanbot_failed_${res.status}`);
      const sentCount =
        json.links?.filter((l) => l.sms.status === 'sent').length ?? 0;
      const failedCount =
        json.links?.filter((l) => l.sms.status === 'failed').length ?? 0;
      const skipCount =
        json.links?.filter((l) => l.sms.status === 'skipped').length ?? 0;
      toast.success(
        t(
          'docs.gegenanbot.done',
          'Gegenanbot hochgeladen. SMS an {{sent}} von {{total}} Käufer gesendet.',
          { sent: sentCount, total: json.buyers_count ?? 0 },
        ),
        { duration: 8000 },
      );
      if (failedCount > 0) {
        toast.error(
          t(
            'docs.gegenanbot.sms_partial_failed',
            '{{count}} SMS fehlgeschlagen — bitte Magic-Link manuell an die betroffenen Käufer senden.',
            { count: failedCount },
          ),
          { duration: 12000 },
        );
      }
      if (skipCount > 0) {
        toast(
          t(
            'docs.gegenanbot.sms_skipped',
            '{{count}} Käufer ohne Telefonnummer — bitte Magic-Link manuell senden.',
            { count: skipCount },
          ),
          { duration: 12000 },
        );
      }
      queryClient.invalidateQueries({
        queryKey: ['documents', 'appointment', appointmentId],
      });
      queryClient.invalidateQueries({ queryKey: ['offer', sourceDoc.offer_id] });
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      onOpenChange(false);
    } catch (cause) {
      setErr((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('docs.gegenanbot.dialog_title', 'Gegenanbot vom Verkäufer hochladen')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <p className="text-ink-3">
            {t(
              'docs.gegenanbot.help',
              'Das vom Verkäufer unterschriebene Gegenanbot-PDF wird hochgeladen. Das ursprüngliche Kaufanbot Nr. {{number}} wird als „Gegenanbot gestellt" markiert. Der Käufer (alle Käufer) erhalten einen Magic-Link zur digitalen Unterzeichnung. Wenn der Käufer unterzeichnet, wird das ursprüngliche Kaufanbot automatisch akzeptiert.',
              { number: sourceDoc.number },
            )}
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="gegenanbot-file">
              {t('docs.gegenanbot.file_label', 'Unterschriebenes Gegenanbot (PDF)')} *
            </Label>
            <Input
              id="gegenanbot-file"
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="gegenanbot-price">
              {t('docs.gegenanbot.price_label', 'Verhandelter Kaufpreis (EUR)')}
            </Label>
            <Input
              id="gegenanbot-price"
              type="text"
              inputMode="numeric"
              value={priceEur}
              onChange={(e) => setPriceEur(e.target.value)}
              placeholder="z.B. 945000"
            />
            <p className="text-[10px] text-ink-3">
              {t(
                'docs.gegenanbot.price_hint',
                'Wird beim Unterzeichnen als finaler Kaufpreis ins ursprüngliche Kaufanbot übernommen. Optional.',
              )}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="gegenanbot-note">
              {t('docs.gegenanbot.note_label', 'Notiz (optional)')}
            </Label>
            <Textarea
              id="gegenanbot-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t(
                'docs.gegenanbot.note_placeholder',
                'z.B. Verkäufer akzeptiert € 945 000 statt € 900 000 ohne Bedingung.',
              )}
            />
          </div>
          {err && (
            <p className="rounded-md border border-[hsl(var(--danger))] bg-[hsl(var(--danger-bg))] px-3 py-2 text-[hsl(var(--danger))]">
              {err}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Abbrechen')}
          </Button>
          <Button type="button" onClick={submit} disabled={submitting || !file}>
            {submitting
              ? t('common.saving', 'Speichert …')
              : t('docs.gegenanbot.submit', 'Gegenanbot anlegen + SMS senden')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// "Von Zoki vorbereitet" — draft hand-off
// ─────────────────────────────────────────────────────────────────────────
// Zoki runs on the server and cannot render a PDF, so when a makler asks it
// for a Vermittlungsauftrag it records the intent instead of faking one. This
// banner is where that intent becomes a real document: one click opens the
// same generator dialog the toolbar button opens, prefilled with whatever the
// assistant already worked out, and the makler still reviews and confirms
// before anything is generated or sent.
function ZokiDraftBanner({ appointmentId }: { appointmentId: string }) {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { data: drafts = [] } = useDocumentDrafts({ appointmentId });
  const { data: appt } = useAppointment(appointmentId);
  const consume = useConsumeDocumentDraft();
  const dismiss = useDismissDocumentDraft();

  if (drafts.length === 0 || !appt || !profile) return null;

  return (
    <div className="mb-2 space-y-2">
      {drafts.map((d) => {
        const va = (d.metadata?.vermittlungsauftrag ?? {}) as {
          contractVariant?: 'allein' | 'einfach';
          exclusivityMonths?: number;
          provisionNetPercent?: number | null;
        };
        // Only aufnahme / besichtigung run through GenerateDocumentButton;
        // a Kaufanbot draft points at /offers instead.
        const generatable =
          d.type === 'vermittlungsauftrag' || d.type === 'besichtigungsschein';
        return (
          <div
            key={d.id}
            className="rounded-lg border border-[hsl(var(--brand-gold)/0.45)] bg-[hsl(var(--brand-gold)/0.08)] px-3 py-2.5"
          >
            <div className="flex items-start gap-2">
              <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-[hsl(var(--brand-gold-deep))]" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-ink">
                  {t('docs.draft.title', 'Von Zoki vorbereitet')}
                </p>
                {d.summary && <p className="mt-0.5 text-xs text-ink-2">{d.summary}</p>}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {generatable ? (
                    <GenerateDocumentButton
                      appointmentId={appointmentId}
                      appointmentDate={toMs(appt.appointment_date)}
                      type={d.type === 'vermittlungsauftrag' ? 'aufnahme' : 'besichtigung'}
                      clientId={appt.client_id}
                      propertyId={appt.property_id}
                      agentName={profile.full_name}
                      agentPhone={profile.phone}
                      labelOverride={t('docs.draft.generate', 'Jetzt erzeugen')}
                      draftDefaults={{
                        ...(va.contractVariant !== undefined
                          ? { contractVariant: va.contractVariant }
                          : {}),
                        ...(va.exclusivityMonths !== undefined
                          ? { exclusivityMonths: va.exclusivityMonths }
                          : {}),
                        ...(va.provisionNetPercent !== undefined
                          ? { provisionNetPercent: va.provisionNetPercent }
                          : {}),
                      }}
                      onGenerated={(documentId) =>
                        consume.mutate({ draftId: d.id, documentId })
                      }
                    />
                  ) : (
                    <span className="text-xs text-ink-3">
                      {t('docs.draft.offer_hint', 'Kaufanbot unter „Anbote" erzeugen.')}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => dismiss.mutate(d.id)}
                    className="text-xs text-ink-3 underline-offset-2 hover:underline"
                  >
                    {t('docs.draft.dismiss', 'Verwerfen')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
